import { useContext, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CheckIcon from '@mui/icons-material/Check';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import { t } from 'i18next';

import { ILayoutContext, LayoutContext } from '../';

export interface TimezoneEntry {
  ianaId: string;
  display: string;
  gmtOffset: string;
}

interface GroupedTimezoneEntry extends TimezoneEntry {
  groupTitle: string;
}

interface TimezoneFieldProps {
  value: string;
  onChange: (e: TimezoneEntry | null) => void;
}

const TIMEZONE_DATA: TimezoneEntry[] = [
  { ianaId: "Africa/Abidjan", display: "Abidjan (GMT)", gmtOffset: "+00:00" },
  { ianaId: "Africa/Accra", display: "Accra (GMT)", gmtOffset: "+00:00" },
  { ianaId: "Africa/Addis_Ababa", display: "Addis Ababa (East Africa Time)", gmtOffset: "+03:00" },
  { ianaId: "Africa/Algiers", display: "Algiers (Central European Time)", gmtOffset: "+01:00" },
  { ianaId: "Africa/Cairo", display: "Cairo (Eastern European Time)", gmtOffset: "+02:00" },
  { ianaId: "Africa/Casablanca", display: "Casablanca (Western European Time)", gmtOffset: "+00:00" },
  { ianaId: "Africa/Johannesburg", display: "Johannesburg (South African Time)", gmtOffset: "+02:00" },
  { ianaId: "Africa/Lagos", display: "Lagos (West Africa Time)", gmtOffset: "+01:00" },
  { ianaId: "Africa/Nairobi", display: "Nairobi (East Africa Time)", gmtOffset: "+03:00" },
  { ianaId: "Africa/Tunis", display: "Tunis (Central European Time)", gmtOffset: "+01:00" },

  { ianaId: "America/Anchorage", display: "Anchorage (Alaska Time)", gmtOffset: "-09:00" },
  { ianaId: "America/Argentina/Buenos_Aires", display: "Buenos Aires (Argentina Time)", gmtOffset: "-03:00" },
  { ianaId: "America/Bogota", display: "Bogota (Colombia Time)", gmtOffset: "-05:00" },
  { ianaId: "America/Caracas", display: "Caracas (Venezuela Time)", gmtOffset: "-04:00" },
  { ianaId: "America/Chicago", display: "Chicago (Central Time)", gmtOffset: "-06:00" },
  { ianaId: "America/Denver", display: "Denver (Mountain Time)", gmtOffset: "-07:00" },
  { ianaId: "America/Halifax", display: "Halifax (Atlantic Time)", gmtOffset: "-04:00" },
  { ianaId: "America/Los_Angeles", display: "Los Angeles (Pacific Time)", gmtOffset: "-08:00" },
  { ianaId: "America/Mexico_City", display: "Mexico City (Central Time)", gmtOffset: "-06:00" },
  { ianaId: "America/New_York", display: "New York (Eastern Time)", gmtOffset: "-05:00" },
  { ianaId: "America/Phoenix", display: "Phoenix (Mountain Time - no DST)", gmtOffset: "-07:00" },
  { ianaId: "America/Santiago", display: "Santiago (Chile Time)", gmtOffset: "-04:00" },
  { ianaId: "America/Sao_Paulo", display: "São Paulo (Brasilia Time)", gmtOffset: "-03:00" },

  { ianaId: "Asia/Baghdad", display: "Baghdad (Arabian Time)", gmtOffset: "+03:00" },
  { ianaId: "Asia/Dhaka", display: "Dhaka (Bangladesh Time)", gmtOffset: "+06:00" },
  { ianaId: "Asia/Dubai", display: "Dubai (Gulf Time)", gmtOffset: "+04:00" },
  { ianaId: "Asia/Hong_Kong", display: "Hong Kong (Hong Kong Time)", gmtOffset: "+08:00" },
  { ianaId: "Asia/Jakarta", display: "Jakarta (Western Indonesia Time)", gmtOffset: "+07:00" },
  { ianaId: "Asia/Jerusalem", display: "Jerusalem (Israel Time)", gmtOffset: "+02:00" },
  { ianaId: "Asia/Kolkata", display: "Kolkata (India Time)", gmtOffset: "+05:30" },
  { ianaId: "Asia/Seoul", display: "Seoul (Korea Time)", gmtOffset: "+09:00" },
  { ianaId: "Asia/Shanghai", display: "Shanghai (China Time)", gmtOffset: "+08:00" },
  { ianaId: "Asia/Singapore", display: "Singapore (Singapore Time)", gmtOffset: "+08:00" },
  { ianaId: "Asia/Tehran", display: "Tehran (Iran Time)", gmtOffset: "+03:30" },
  { ianaId: "Asia/Tokyo", display: "Tokyo (Japan Time)", gmtOffset: "+09:00" },

  { ianaId: "Europe/Amsterdam", display: "Amsterdam (Central European Time)", gmtOffset: "+01:00" },
  { ianaId: "Europe/Athens", display: "Athens (Eastern European Time)", gmtOffset: "+02:00" },
  { ianaId: "Europe/Berlin", display: "Berlin (Central European Time)", gmtOffset: "+01:00" },
  { ianaId: "Europe/Istanbul", display: "Istanbul (Turkey Time)", gmtOffset: "+03:00" },
  { ianaId: "Europe/Kiev", display: "Kiev (Eastern European Time)", gmtOffset: "+02:00" },
  { ianaId: "Europe/London", display: "London (British Time)", gmtOffset: "+00:00" },
  { ianaId: "Europe/Madrid", display: "Madrid (Central European Time)", gmtOffset: "+01:00" },
  { ianaId: "Europe/Moscow", display: "Moscow (Moscow Time)", gmtOffset: "+03:00" },
  { ianaId: "Europe/Paris", display: "Paris (Central European Time)", gmtOffset: "+01:00" },
  { ianaId: "Europe/Rome", display: "Rome (Central European Time)", gmtOffset: "+01:00" },

  { ianaId: "Pacific/Auckland", display: "Auckland (New Zealand Time)", gmtOffset: "+12:00" },
  { ianaId: "Pacific/Fiji", display: "Fiji (Fiji Time)", gmtOffset: "+12:00" },
  { ianaId: "Pacific/Guam", display: "Guam (Chamorro Time)", gmtOffset: "+10:00" },
  { ianaId: "Pacific/Honolulu", display: "Honolulu (Hawaii Time)", gmtOffset: "-10:00" },
  { ianaId: "Pacific/Noumea", display: "Noumea (New Caledonia Time)", gmtOffset: "+11:00" },
  { ianaId: "Pacific/Pago_Pago", display: "Pago Pago (Samoa Time)", gmtOffset: "-11:00" },
  { ianaId: "Pacific/Port_Moresby", display: "Port Moresby (Papua New Guinea Time)", gmtOffset: "+10:00" },
  { ianaId: "Pacific/Tahiti", display: "Tahiti (French Polynesia Time)", gmtOffset: "-10:00" }
];

interface TimezoneSelectorProps {
  value: string;
  onSelect: (timezone: TimezoneEntry) => void;
}

export const TimezoneSelector = ({ value, onSelect }: TimezoneSelectorProps) => {
  const groupedTimezones: GroupedTimezoneEntry[] = TIMEZONE_DATA.map((option) => {
    const slashIndex = option.ianaId.indexOf('/');

    return {
      groupTitle: option.ianaId.substring(0, slashIndex),
      ...option,
    };
  });

  const [selectedTimezone, setSelectedTimezone] = useState<GroupedTimezoneEntry | undefined | null>
    (groupedTimezones.filter(option => option.ianaId === value)[0]);

  return (
    <>
      <Autocomplete
        options={groupedTimezones.sort((a, b) => -b.groupTitle.localeCompare(a.groupTitle))}
        groupBy={(option) => option.groupTitle}
        getOptionLabel={(option) => option.ianaId}
        renderInput={(params) => <TextField {...params} label={t("select-timezone")} />}
        value={selectedTimezone}
        onChange={(_event: any, newValue: GroupedTimezoneEntry | null) => {
          setSelectedTimezone(newValue);
        }} />

      {selectedTimezone && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('timezone')}: {selectedTimezone.ianaId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('gmt-offset')}: {selectedTimezone.gmtOffset}
          </Typography>
        </Box>
      )}

      <FormControl fullWidth>
        <Button
          className='confirm-button'
          variant="contained"
          disabled={!selectedTimezone}
          endIcon={<CheckIcon />}
          onClick={() => onSelect(selectedTimezone as TimezoneEntry)}>
          {t('confirm')}
        </Button>
      </FormControl>
    </>
  );
};

const TimezoneField = (props: TimezoneFieldProps) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const [value, setValue] = useState<string>(props.value);

  const onChangeTimezone = () => {
    layoutContext.dialog.show({
      title: t('timezone'),
      className: "timezone-field-dialog",
      content: <TimezoneSelector value={value} onSelect={t => {
        props.onChange(t);
        setValue(t.ianaId);
        layoutContext.dialog.hide();
      }} />
    })
  }

  return (
    <div className="timezone-field outlined-with-label">
      <label>{t("timezone")}</label>

      <span>{value.toString()}</span>

      <Button variant="outlined" onClick={onChangeTimezone}>{t('change')}</Button>
    </div>
  );
}

export default TimezoneField;
